/**
 * <ActionFooter> — shared sticky action-bar shell.
 *
 * Run 2 (mobile-remediation) primitive. Resolves audit findings F-01/F-02
 * (overlay footers sitting under the iOS keyboard/URL bar with no
 * safe-area padding) and gives full-page mobile forms the same
 * always-reachable action bar without hand-rolling `fixed bottom-0` +
 * `env()` math per screen.
 *
 * Two variants:
 *   - `panel` (default) — for use INSIDE an overlay (Modal/Drawer). Renders
 *     inline at the bottom of the flex column; the overlay shell owns the
 *     scrolling body above it, so `position: sticky` is enough to keep the
 *     footer visible without covering content. This is what
 *     `Modal.Footer`/`Drawer.Footer` render under the hood.
 *   - `page` — for a full-page mobile form (no overlay ancestor). Pins to
 *     the actual bottom of the *visual* viewport (not just the layout
 *     viewport) so the bar tracks the on-screen keyboard instead of being
 *     covered by it — see `useVisualViewport` (F-08).
 *
 * Safe-area padding is ADDITIVE via an arbitrary-value `calc()` (not a
 * standalone `.pb-safe` utility) so it stacks correctly with the existing
 * `py-3` regardless of CSS layer/source order — same idiom already used in
 * `base.css` for the editorial auth screens.
 *
 * Footer order (Cancel-left / primary-right) is the caller's
 * responsibility — this shell only owns the chrome, not action ordering.
 *
 * Spec: docs/audits/MOBILE_AUDIT.md §Primitives — ActionFooter.
 */
import { type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { useVisualViewport } from '../../hooks/useVisualViewport';

export type ActionFooterVariant = 'panel' | 'page';

export interface ActionFooterProps extends HTMLAttributes<HTMLDivElement> {
  variant?: ActionFooterVariant;
  children: ReactNode;
}

export function ActionFooter({ variant = 'panel', className, style, children, ...rest }: ActionFooterProps) {
  // Only the `page` variant reads the visual viewport — `panel` lives inside
  // an overlay whose Body already scrolls independently, so its sticky
  // footer never gets covered. Gate the subscription (the hook's `enabled`
  // contract) so a `panel` footer inside Modal/Drawer doesn't hold a second
  // live `visualViewport` listener next to the overlay shell's own.
  const vv = useVisualViewport(variant === 'page');

  const base = cn(
    'flex items-center justify-end gap-2 border-t border-border-subtle bg-surface-elevated px-5 py-3',
    // Additive safe-area bottom padding — resolves to 0 on non-notched
    // devices, so this never changes desktop or older-browser layout.
    'pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]',
  );

  if (variant === 'page') {
    return (
      <div
        role="group"
        aria-label="Page actions"
        className={cn(base, 'fixed inset-x-0 z-40 shadow-footer', className)}
        style={{
          // Pin to the bottom of the VISUAL viewport (tracks the keyboard)
          // instead of `bottom: 0` (layout viewport — gets covered by the
          // keyboard on iOS/Android). `keyboardInset` is already clamped
          // to ≥0 and is 0 when `visualViewport` is unavailable/unmeasured.
          bottom: vv.keyboardInset,
          ...style,
        }}
        {...rest}
      >
        {children}
      </div>
    );
  }

  return (
    <div role="group" aria-label="Dialog actions" className={cn(base, 'sticky bottom-0', className)} style={style} {...rest}>
      {children}
    </div>
  );
}

ActionFooter.displayName = 'ActionFooter';

export default ActionFooter;
